import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DataApi from '../../api/DataApi';
import LookupLayout from '../layout/LookupLayout';
import {
  formatRunAsLabel,
  getLoggedInUserId,
  loggedInUserRunAsLabel,
  pickRecordId,
  runningUserApiRowToOption,
} from '../../utils/dashboardRunUserLabel';
import { getLicenseTypeCodeFromSession } from '../../utils/entitlements';
import {
  dashboardFormFromRecord,
  formatDashboardOwner,
  formatDashboardOwnerLabel,
  normalizeDashboardPayload,
  resolveDashboardOwnerId,
} from './dashboardFormUtils';
import './css/DashboardManager.css';

const DashboardSettingsModal = ({
  show,
  dashboard,
  onClose,
  onSaved,
}) => {
  const canChangeDashboardOwner = getLicenseTypeCodeFromSession() === 'ADMIN';

  const [formData, setFormData] = useState(dashboardFormFromRecord(dashboard));
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [runningUserOptions, setRunningUserOptions] = useState([]);
  const [runningUsersLoading, setRunningUsersLoading] = useState(false);

  const dashboardId = dashboard ? pickRecordId(dashboard) : '';

  const loadRunningUserOptions = useCallback(async () => {
    setRunningUsersLoading(true);
    try {
      const dashboardApi = new DataApi('dashboard');
      const res = await dashboardApi.get('/running-user-options?limit=500');
      const rows = res?.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
      const opts = rows.map(runningUserApiRowToOption).filter(Boolean);
      const byId = new Map(opts.map((o) => [o.id, o]));
      const me = getLoggedInUserId();
      if (me && !byId.has(me)) {
        byId.set(me, { id: me, label: loggedInUserRunAsLabel() });
      }
      setRunningUserOptions(
        Array.from(byId.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        )
      );
    } catch (e) {
      console.error('Failed to load users for dashboard running user:', e);
      const me = getLoggedInUserId();
      setRunningUserOptions(me ? [{ id: me, label: loggedInUserRunAsLabel() }] : []);
    } finally {
      setRunningUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show || !dashboard) return;
    setFormData(dashboardFormFromRecord(dashboard));
    setFormError(null);
    loadRunningUserOptions();
  }, [show, dashboard?.id, loadRunningUserOptions]);

  useEffect(() => {
    if (!show) return;
    const ownerId = resolveDashboardOwnerId(dashboard);
    if (!ownerId) return;
    const existing = formatDashboardOwnerLabel(dashboard);
    if (existing) return;
    const match = runningUserOptions.find((o) => String(o.id) === String(ownerId));
    if (!match) return;
    setFormData((prev) =>
      String(prev.owner_id) === String(ownerId) && !prev.owner_label
        ? { ...prev, owner_label: match.label }
        : prev
    );
  }, [show, dashboard, runningUserOptions]);

  useEffect(() => {
    if (!show) return;
    const id = formData.running_user_id && String(formData.running_user_id).trim();
    if (!id) return;
    setRunningUserOptions((prev) => {
      if (prev.some((o) => o.id === id)) return prev;
      return [
        ...prev,
        { id, label: formatRunAsLabel(`User ${id.slice(0, 8)}…`, '—', '—') },
      ].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    });
  }, [show, formData.running_user_id]);

  const runningUserSelectOptions =
    runningUserOptions.length > 0
      ? runningUserOptions
      : (() => {
          const me = getLoggedInUserId();
          return me ? [{ id: me, label: loggedInUserRunAsLabel() }] : [];
        })();

  const handleOwnerLookupChange = (userId, userLabel) => {
    const uid = userId ? String(userId).trim() : '';
    setFormData((prev) => ({
      ...prev,
      owner_id: uid,
      owner_label: uid ? (userLabel || '').trim() : '',
    }));
  };

  const handleViewTypeChange = (viewType) => {
    setFormData((prev) => {
      const next = { ...prev, view_type: viewType };
      if (viewType === 'STATIC_USER') {
        const cur = prev.running_user_id && String(prev.running_user_id).trim();
        next.running_user_id = cur || getLoggedInUserId() || '';
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!dashboardId) return;
    try {
      setSaving(true);
      setFormError(null);
      const payload = normalizeDashboardPayload(formData);
      if (payload.view_type === 'STATIC_USER' && !payload.running_user_id) {
        setFormError('Please select a user for “Fixed user” dashboards.');
        return;
      }
      if (canChangeDashboardOwner && !payload.ownerid) {
        setFormError('Please select an owner for this dashboard.');
        return;
      }
      const api = new DataApi('dashboard');
      const res = await api.update(payload, dashboardId);
      if (res?.data?.success) {
        let refreshed = res?.data?.data;
        try {
          const getRes = await api.get(`/${dashboardId}`);
          if (getRes?.data?.data) refreshed = getRes.data.data;
        } catch (refetchErr) {
          console.warn('Could not refetch dashboard after save:', refetchErr);
        }
        onSaved?.(refreshed);
        onClose?.();
      } else {
        setFormError('Failed to update dashboard');
      }
    } catch (err) {
      console.error('Error updating dashboard:', err);
      setFormError(err?.response?.data?.message || 'Failed to update dashboard.');
    } finally {
      setSaving(false);
    }
  };

  if (!show || !dashboard) return null;

  const modal = (
    <div className="entity-assign-modal-scope dashboard-manager">
      <div className="dashboard-modal-overlay" onClick={onClose}>
        <div
          className="dashboard-modal dashboard-modal-wide"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dashboard-modal-header">
            <h2>Dashboard settings</h2>
            <button type="button" className="dashboard-close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          <form onSubmit={handleSave} className="dashboard-modal-form">
            {formError && (
              <div className="dashboard-form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="dashboard-form-group">
              <label htmlFor="settings-label">Dashboard label *</label>
              <input
                type="text"
                id="settings-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                required
              />
            </div>
            <div className="dashboard-form-group">
              <label htmlFor="settings-name">Dashboard name</label>
              <input
                type="text"
                id="settings-name"
                value={formData.name}
                disabled
                readOnly
                className="dashboard-name-disabled"
              />
            </div>
            <div className="dashboard-form-group">
              <label htmlFor="settings-description">Description</label>
              <textarea
                id="settings-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows="3"
              />
            </div>
            {canChangeDashboardOwner ? (
              <div className="dashboard-form-group">
                <label htmlFor="settings-owner-lookup">Owner *</label>
                <LookupLayout
                  key={`owner-${dashboardId}-${formData.owner_id || 'none'}`}
                  objectName="user"
                  searchFields="firstname,lastname,email,username"
                  displayFields="firstname,lastname,email"
                  value={formData.owner_id}
                  defaultLabel={formData.owner_label}
                  onChange={handleOwnerLookupChange}
                  placeholder="Search owner by name or email…"
                  dropdownZIndex={10100}
                  activeUsersOnly
                />
              </div>
            ) : (
              <div className="dashboard-form-group">
                <label>Owner</label>
                <p className="dashboard-form-hint mb-0">{formatDashboardOwner(dashboard)}</p>
              </div>
            )}
            <div className="dashboard-form-group">
              <label htmlFor="settings-view_type">Running user (view as)</label>
              <select
                id="settings-view_type"
                value={formData.view_type}
                onChange={(e) => handleViewTypeChange(e.target.value)}
                className="dashboard-form-select"
              >
                <option value="STATIC_USER">Fixed user</option>
                <option value="LOGGED_IN_USER">Logged-in viewer</option>
              </select>
            </div>
            {formData.view_type === 'STATIC_USER' && (
              <div className="dashboard-form-group">
                <label htmlFor="settings-running_user_id">Run dashboard as user *</label>
                <select
                  id="settings-running_user_id"
                  value={formData.running_user_id}
                  onChange={(e) =>
                    setFormData({ ...formData, running_user_id: e.target.value })
                  }
                  className="dashboard-form-select"
                  required
                  disabled={runningUserSelectOptions.length === 0}
                >
                  {runningUserSelectOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {runningUsersLoading && (
                  <p className="small text-muted mt-1 mb-0">Loading user list…</p>
                )}
              </div>
            )}

            <div className="dashboard-modal-actions">
              <button type="button" className="dashboard-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="dashboard-save-btn" disabled={saving}>
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DashboardSettingsModal;
